import { LedgerSigner, canonicalJson, cid } from '@vibetime/runtime/ledger/signer';
import { BENCH_PROMPTS } from './ollama-inference.js';
import { publicSiteOrigin } from './site-origin.js';
import type { BenchmarkResult, MachineProfile, PublishedReceipt } from './types.js';

const SESSION_SIGNER = LedgerSigner.generate();
const RECEIPT_CACHE = new Map<string, SignedReceipt>();

export interface ReplayMetadata {
  prompt: string;
  ollamaTag?: string;
  runtime: BenchmarkResult['runtime'];
  quantization: BenchmarkResult['quantization'];
  taskCategory: BenchmarkResult['taskCategory'];
  deterministicSeed?: number;
  apiEndpoint: string;
  instructions: string;
}

export interface SignedReceipt {
  receiptId: string;
  contentId: string;
  publicKeyHex: string;
  signature: string;
  signedAt: number;
  payload: {
    machine: MachineProfile;
    result: BenchmarkResult;
    replay: ReplayMetadata;
    signer: string;
    version: 2;
  };
  shareUrl: string;
  exportJson: string;
}

interface ReceiptRecord {
  receiptId: string;
  contentId: string;
  publicKeyHex: string;
  signature: string;
  signedAt: number;
  payload: SignedReceipt['payload'];
}

function buildReplay(result: BenchmarkResult): ReplayMetadata {
  return {
    prompt: BENCH_PROMPTS[result.taskCategory],
    runtime: result.runtime,
    quantization: result.quantization,
    taskCategory: result.taskCategory,
    apiEndpoint: result.runtime === 'ollama' ? 'http://127.0.0.1:11434/api/generate' : 'varies',
    instructions:
      result.runtime === 'ollama'
        ? 'POST the prompt to /api/generate with { model, prompt, stream: false, options: { temperature: 0.2, num_predict: 256 } } and compare eval_count/eval_duration against the result fields.'
        : 'Reproduce by running the same prompt through the named runtime with the stated quantization.',
  };
}

export function signBenchmarkReceipt(result: BenchmarkResult, machine: MachineProfile): SignedReceipt {
  const cached = RECEIPT_CACHE.get(result.id);
  if (cached && cached.payload.machine.id === machine.id) return cached;

  const payload = {
    machine,
    result,
    replay: buildReplay(result),
    signer: 'vibetime:deck',
    version: 2 as const,
  };
  const canonical = canonicalJson(payload);
  const contentId = cid(payload);
  const signature = SESSION_SIGNER.sign(canonical);
  const publicKeyHex = SESSION_SIGNER.publicKeyHex();
  const signedAt = Date.now();
  const receipt = hydrateReceipt({
    receiptId: receiptIdForResult(result),
    contentId,
    publicKeyHex,
    signature,
    signedAt,
    payload,
  });
  RECEIPT_CACHE.set(result.id, receipt);
  return receipt;
}

export function exportReceiptAs(
  receipt: SignedReceipt,
  format: 'json' | 'csv' | 'markdown' | 'slsa',
): string {
  if (format === 'json') return receipt.exportJson;
  if (format === 'csv') return exportCsv(receipt);
  if (format === 'markdown') return exportMarkdown(receipt);
  if (format === 'slsa') return exportSlsa(receipt);
  return receipt.exportJson;
}

function exportCsv(receipt: SignedReceipt): string {
  const r = receipt.payload.result;
  const m = receipt.payload.machine;
  const headers = [
    'receiptId',
    'contentId',
    'machineId',
    'modelId',
    'quantization',
    'runtime',
    'taskCategory',
    'tokensPerSecond',
    'latencyMs',
    'timeToFirstTokenMs',
    'qualityScore',
    'passRate',
    'stabilityScore',
    'toolUseSuccess',
    'diffQuality',
    'repairSuccess',
    'agentCompletionScore',
    'benchConfidence',
    'memoryUseGb',
    'vramUseGb',
    'source',
    'publicKey',
    'signature',
  ];
  const row = [
    receipt.receiptId,
    receipt.contentId,
    m.id,
    r.modelId,
    r.quantization,
    r.runtime,
    r.taskCategory,
    r.tokensPerSecond,
    r.latencyMs,
    r.timeToFirstTokenMs,
    r.qualityScore,
    r.passRate,
    r.stabilityScore,
    r.toolUseSuccess,
    r.diffQuality,
    r.repairSuccess,
    r.agentCompletionScore,
    r.benchConfidence,
    r.memoryUseGb,
    r.vramUseGb,
    r.source,
    receipt.publicKeyHex,
    receipt.signature,
  ];
  return `${headers.join(',')}\n${row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')}\n`;
}

function exportMarkdown(receipt: SignedReceipt): string {
  const r = receipt.payload.result;
  const m = receipt.payload.machine;
  return `# Vibetime Lab receipt · ${receipt.receiptId}

**Model:** ${r.modelId} · ${r.quantization} · ${r.runtime}
**Machine:** ${m.nickname}
**Task:** ${r.taskCategory}
**Source:** ${r.source}
**Signed at:** ${new Date(receipt.signedAt).toISOString()}

## Metrics
| Metric | Value |
| --- | --- |
| Tokens/sec | ${r.tokensPerSecond} |
| First token | ${r.timeToFirstTokenMs} ms |
| Quality | ${r.qualityScore} |
| Pass rate | ${(r.passRate * 100).toFixed(0)}% |
| Stability | ${(r.stabilityScore * 100).toFixed(0)}% |
| Tool-use | ${(r.toolUseSuccess * 100).toFixed(0)}% |
| Diff quality | ${r.diffQuality} |
| Repair success | ${(r.repairSuccess * 100).toFixed(0)}% |
| Agent completion | ${r.agentCompletionScore} |
| Bench confidence | ${(r.benchConfidence * 100).toFixed(0)}% |
| Memory used | ${r.memoryUseGb} GB |
| VRAM used | ${r.vramUseGb} GB |

## Proof
- **Content CID:** \`${receipt.contentId}\`
- **Ed25519 signature:** \`${receipt.signature}\`
- **Public key:** \`${receipt.publicKeyHex}\`
- **Share URL:** ${receipt.shareUrl}

## Replay
\`\`\`
${receipt.payload.replay.instructions}
\`\`\`
`;
}

function exportSlsa(receipt: SignedReceipt): string {
  const statement = {
    _type: 'https://in-toto.io/Statement/v1',
    subject: [
      {
        name: `vibetime-lab-bench/${receipt.payload.result.modelId}`,
        digest: { sha256: receipt.contentId.replace('sha256:', '') },
      },
    ],
    predicateType: `${publicSiteOrigin()}/lab/benchmark/v1`,
    predicate: {
      machine: receipt.payload.machine,
      result: receipt.payload.result,
      replay: receipt.payload.replay,
      signer: receipt.payload.signer,
      signedAt: new Date(receipt.signedAt).toISOString(),
    },
  };
  const envelope = {
    payloadType: 'application/vnd.in-toto+json',
    payload: encodeBase64(JSON.stringify(statement)),
    signatures: [{ keyid: receipt.publicKeyHex, sig: receipt.signature }],
  };
  return JSON.stringify(envelope, null, 2);
}

function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  const g = globalThis as { btoa?: (s: string) => string };
  if (g.btoa) return g.btoa(binary);
  const nodeBuffer = (
    globalThis as { Buffer?: { from: (s: string, enc: string) => { toString: (e: string) => string } } }
  ).Buffer;
  if (nodeBuffer) return nodeBuffer.from(text, 'utf8').toString('base64');
  return binary;
}

export function verifyReceipt(receipt: SignedReceipt): boolean {
  const canonical = canonicalJson(receipt.payload);
  return LedgerSigner.verify(canonical, receipt.signature, receipt.publicKeyHex);
}

export function receiptIdForResult(result: Pick<BenchmarkResult, 'id'>): string {
  return `vt-bench-${result.id}`;
}

export function receiptShareSlug(receipt: SignedReceipt): string {
  return encodeBase64Url(canonicalJson(toReceiptRecord(receipt)));
}

export function decodeReceiptSlug(slug: string): SignedReceipt | null {
  try {
    const decoded = decodeBase64Url(slug);
    const parsed = JSON.parse(decoded) as ReceiptRecord;
    if (!parsed?.receiptId || !parsed?.contentId || !parsed?.signature || !parsed?.payload) return null;
    const receipt = hydrateReceipt(parsed);
    return verifyReceipt(receipt) ? receipt : null;
  } catch {
    return null;
  }
}

export function publishedReceiptRecord(r: SignedReceipt): PublishedReceipt {
  return {
    id: r.receiptId,
    benchmarkId: r.payload.result.id,
    createdAt: r.signedAt,
    publisher: 'vibetime:deck',
    ledgerHash: r.contentId,
    shareUrl: r.shareUrl,
  };
}

function toReceiptRecord(receipt: SignedReceipt): ReceiptRecord {
  return {
    receiptId: receipt.receiptId,
    contentId: receipt.contentId,
    publicKeyHex: receipt.publicKeyHex,
    signature: receipt.signature,
    signedAt: receipt.signedAt,
    payload: receipt.payload,
  };
}

function hydrateReceipt(record: ReceiptRecord): SignedReceipt {
  const slug = encodeBase64Url(canonicalJson(record));
  const shareUrl = `${publicSiteOrigin()}/#/lab/receipts/${slug}`;
  const exportJson = JSON.stringify({ ...record, shareUrl }, null, 2);
  return { ...record, shareUrl, exportJson };
}

function encodeBase64Url(text: string): string {
  return encodeBase64(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodeBase64Url(text: string): string {
  const normalized = text.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const g = globalThis as {
    atob?: (s: string) => string;
    Buffer?: { from: (s: string, enc: string) => { toString: (e: string) => string } };
  };
  if (g.atob) {
    const binary = g.atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  if (g.Buffer) return g.Buffer.from(padded, 'base64').toString('utf8');
  return padded;
}
